import { endOfDay, isValid, parseISO } from 'date-fns';

import { toLocalMidnightDate } from './localDateOnly';

/**
 * The instant a tournament actually ends.
 *
 * The backend derives `endsAt` from the close of the tournament's last scheduled match — in any
 * format with a bracket, that is the final. It comes back null while the tournament has no fixture
 * yet, and while every match still holds the provisional time the generator gave it.
 *
 * `endDate` cannot stand in for it: the column holds 00:00 of the last day, so counting down to it
 * would treat the tournament as over for its whole final day. The fallback is the end of that day
 * instead, which is coarse but never early.
 *
 * The two branches do not agree on timezone: `endsAt` arrives already converted to the tournament's
 * zone, while the fallback's `endOfDay` lands in the viewer's. A manager abroad sees the fallback
 * skewed by the offset — the same gap every other date in this app has, since none of them carry
 * `tournament.timezone` through. Worth closing across the board, not here alone.
 */
export function getTournamentEndInstant(
  endDate: string | null | undefined,
  endsAt?: string | null,
): Date | null {
  if (endsAt) {
    const parsed = parseISO(endsAt);
    if (isValid(parsed)) return parsed;
  }

  const endDay = toLocalMidnightDate(endDate);
  return endDay ? endOfDay(endDay) : null;
}
